import React, { useEffect, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { processTranscript } from '@/services/aiProcessing';
import { useToast } from '@/hooks/use-toast';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { X, Save, Mic } from 'lucide-react';
import { RateLimitError, AuthenticationError, OpenAIError } from '@/services/openai';

const ListeningOverlay: React.FC = () => {
  const { 
    isListening, 
    transcript, 
    setTranscript,
    processingTranscript,
    setProcessingTranscript,
    setIsListening,
    addTask
  } = useApp();
  
  const { toast } = useToast();
  const [dots, setDots] = useState('');
  const [editableTranscript, setEditableTranscript] = useState('');
  
  // Update editable transcript when the actual transcript changes
  useEffect(() => {
    if (transcript) {
      setEditableTranscript(transcript);
    }
  }, [transcript]);

  // Loading dots animation
  useEffect(() => {
    if (isListening || processingTranscript) {
      const interval = setInterval(() => {
        setDots((prevDots) => (prevDots.length >= 3 ? '' : prevDots + '.'));
      }, 500);
      return () => clearInterval(interval);
    } else {
      setDots('');
    }
  }, [isListening, processingTranscript]);

  // Auto-scroll textarea as content grows
  useEffect(() => {
    const textarea = document.querySelector('textarea');
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, [editableTranscript]);

  const handleSave = async () => {
    if (!editableTranscript.trim()) {
      toast({
        title: "Empty Input",
        description: "Please say or type something first.",
        variant: "destructive"
      });
      return;
    }

    setProcessingTranscript(true);
    try {
      const taskInfo = await processTranscript(editableTranscript);
      addTask({
        title: taskInfo.title,
        category: taskInfo.category,
        completed: false,
        dueDate: taskInfo.dueDate ? new Date(taskInfo.dueDate) : null,
        description: taskInfo.description || '',
        isRecurring: taskInfo.isRecurring || false
      });
      
      toast({
        title: "Task added!",
        description: `"${taskInfo.title}" has been added to your ${taskInfo.category} tasks.`
      });
      
      // Clear everything after successful save
      setTranscript('');
      setEditableTranscript('');
      setIsListening(false);
    } catch (error) {
      console.error("Error processing transcript:", error);
      
      if (error instanceof RateLimitError) {
        toast({
          title: "Rate Limit Exceeded",
          description: "You've reached the maximum number of voice tasks for now. Please try again later.",
          variant: "destructive"
        });
      } else if (error instanceof AuthenticationError) {
        toast({
          title: "Authentication Required",
          description: "Please sign in to create voice tasks.",
          variant: "destructive"
        });
      } else if (error instanceof OpenAIError) {
        toast({
          title: "Processing Error",
          description: error.message || "Failed to process your voice input. Please try again.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Unexpected Error",
          description: "Something went wrong. Please try again.",
          variant: "destructive"
        });
      }
    } finally {
      setProcessingTranscript(false);
    }
  };

  const handleCancel = () => {
    setTranscript('');
    setEditableTranscript('');
    setIsListening(false);
    setProcessingTranscript(false);
  };
  
  if (!isListening && !processingTranscript && !editableTranscript) return null;
  
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-purple-500/30 via-blue-500/30 to-teal-500/30 backdrop-blur-lg flex items-center justify-center z-50 overscroll-none touch-none">
      <div className="relative w-[90%] max-w-md">
        {/* Card with enhanced glass effect */}
        <div className="bg-white/50 backdrop-blur-2xl border border-white/40 rounded-[2rem] shadow-2xl overflow-hidden overscroll-none touch-none">
          {/* Header with animated gradient */}
          <div 
            className="px-8 py-6 border-b border-white/30 flex justify-between items-center bg-gradient-to-r from-purple-500/60 via-blue-500/60 to-purple-500/60 bg-[length:200%_100%] animate-gradient"
          >
            <h2 className="text-3xl font-bold text-white drop-shadow-md">
              {processingTranscript ? 'Processing' : 'Listening'}{dots}
            </h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCancel}
              className="text-white hover:text-white hover:bg-white/40 rounded-full transition-all duration-200"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="p-8 space-y-6 bg-white/20">
            {/* Enhanced Microphone Animation */}
            {isListening && (
              <div className="flex justify-center mb-6">
                <div className="relative">
                  {/* Outer ripple */}
                  <div className="absolute inset-[-10px] bg-purple-500/30 rounded-full animate-ping-slow"></div>
                  {/* Middle ripple */}
                  <div className="absolute inset-[-5px] bg-purple-500/40 rounded-full animate-ping-medium"></div>
                  {/* Inner container */}
                  <div className="relative w-13 h-13 rounded-full bg-gradient-to-br from-purple-500/70 to-blue-500/70 p-1 shadow-lg">
                    {/* Mic container with rotating gradient border */}
                    <div className="w-full h-full rounded-full bg-gradient-to-br from-purple-400/60 to-blue-400/60 flex items-center justify-center relative overflow-hidden">
                      {/* Animated gradient background */}
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/60 via-blue-500/60 to-purple-500/60 bg-[length:200%_100%] animate-gradient"></div>
                      <Mic className="w-6 h-6 text-white relative z-10 drop-shadow-md" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Enhanced Textarea with real-time updates */}
            <div className="relative">
              <Textarea
                value={editableTranscript}
                onChange={(e) => setEditableTranscript(e.target.value)}
                placeholder={processingTranscript ? "Creating your task..." : "Say something..."}
                className="min-h-[120px] bg-gray-50/95 text-gray-900 font-bold text-lg placeholder:text-gray-400 border-white/40 focus:border-purple-400/80 rounded-2xl resize-none p-4 shadow-inner transition-all duration-200"
                disabled={processingTranscript}
                style={{ height: 'auto' }}
              />
              {/* Enhanced glow effect */}
              <div className="absolute inset-0 -z-10 bg-purple-500/30 rounded-2xl blur-xl"></div>
            </div>

            {/* Enhanced Buttons */}
            <div className="flex justify-end gap-4">
              <Button
                variant="ghost"
                onClick={handleCancel}
                className="text-white hover:text-white hover:bg-white/30 px-6 rounded-xl transition-all duration-200 shadow-sm"
                disabled={processingTranscript}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-medium px-6 rounded-xl shadow-lg shadow-purple-500/40 transition-all duration-200 relative group"
                disabled={processingTranscript || !editableTranscript.trim()}
              >
                <span className="relative z-10">
                  {processingTranscript ? 'Processing...' : 'Create Task'}
                </span>
                {/* Button hover effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListeningOverlay;
